import React, { useState, useRef } from 'react';
import Papa from 'papaparse';
import * as XLSX from 'xlsx';

import AgModalWindow from '@/components/common/ModalWindow/AgModalWindow';
import Button, { ButtonStyleType } from '@/components/common/Button/Button';
import { TableGrid } from '@/components/common/TableGrid/TableGrid';
import { 
  parseFile, 
  generateCSV, 
  generateExcel, 
  downloadFile, 
  VarBillingRow, 
  ProcessingStatus 
} from './utils/fileParser';
import NoRowsOverlay from './components/NoRowsOverlay';
import ColumnMappingModal from './components/ColumnMappingModal';
import { notificationService } from '@/services/notification/notification.client';
import useVarBillingUploadFile from '../hooks/useVarBillingUploadFile';
import styles from './file-upload.module.scss';

interface IFileUpload {
  onUploadSuccess: (data: { uploadId: string; fileName: string; numberOfRows: number }) => void;
  isOpen: boolean;
  onClose: () => void;
}

export default function FileUpload({ onUploadSuccess, isOpen, onClose }: IFileUpload) {
  const [tableData, setTableData] = useState<VarBillingRow[]>([]);
  const [originalFile, setOriginalFile] = useState<File | null>(null);
  const [originalFileFormat, setOriginalFileFormat] = useState<'csv' | 'excel' | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [isMaximized, setIsMaximized] = useState(false);
  const [showColumnMapping, setShowColumnMapping] = useState(false);
  const [csvHeaders, setCsvHeaders] = useState<string[]>([]);
  const [sampleData, setSampleData] = useState<string[][]>([]);
  const [columnMappings, setColumnMappings] = useState<Map<string, string>>(new Map());
  const fileInputRef = useRef<HTMLInputElement>(null);
  const gridRef = useRef<any>(null);

  const { onFileUpload } = useVarBillingUploadFile();

  const columnDefs = [
    { headerName: 'External Contract ID', field: 'externalContractId', sortable: false, filter: false, width: 180 },
    { headerName: 'Customer Code', field: 'customerCode', sortable: false, filter: false, width: 150 },
    { headerName: 'Product Code', field: 'productCode', sortable: false, filter: false, width: 150 },
    { headerName: 'Quantity', field: 'quantity', sortable: false, filter: false, width: 100 },
    { headerName: 'Price', field: 'price', sortable: false, filter: false, width: 100 },
    { headerName: 'Net', field: 'net', sortable: false, filter: false, width: 100 },
    { headerName: 'Comment', field: 'comment', sortable: false, filter: false, width: 200 },
    { headerName: 'Errors', field: 'errors', sortable: false, filter: false, width: 400 },
  ];

  const extractHeadersAndSamples = (file: File): Promise<{ headers: string[]; samples: string[][] }> => {
    return new Promise((resolve, reject) => {
      const fileExtension = file.name.split('.').pop()?.toLowerCase();
      
      if (fileExtension === 'csv') {
        Papa.parse(file, {
          preview: 5,
          complete: (results) => {
            if (results.data && results.data.length > 0) {
              const headers = results.data[0] as string[];
              const samples = results.data.slice(1) as string[][];
              resolve({ headers, samples });
            } else {
              reject(new Error('No data found in file'));
            }
          },
          error: (error) => reject(error)
        });
      } else if (fileExtension === 'xlsx' || fileExtension === 'xls') {
        const reader = new FileReader();
        reader.onload = (e) => {
          try {
            const data = new Uint8Array(e.target?.result as ArrayBuffer);
            const workbook = XLSX.read(data, { type: 'array' });
            const firstSheetName = workbook.SheetNames[0];
            const worksheet = workbook.Sheets[firstSheetName];
            const jsonData = XLSX.utils.sheet_to_json(worksheet, { header: 1 }) as any[][];
            
            if (!jsonData || jsonData.length === 0) {
              reject(new Error('No data found in Excel file'));
              return;
            }
            
            const headers = jsonData[0].map((h: any) => String(h || ''));
            const samples = jsonData.slice(1, 5).map((row: any[]) => 
              row.map((cell: any) => String(cell || ''))
            );
            
            resolve({ headers, samples });
          } catch (error: any) {
            reject(new Error(`Failed to parse Excel file: ${error.message}`));
          }
        };
        reader.onerror = () => reject(new Error('Failed to read Excel file'));
        reader.readAsArrayBuffer(file);
      } else {
        reject(new Error('Unsupported file format. Please upload CSV or Excel files.'));
      }
    });
  };

  const handleFileUpload = async (file: File) => {
    try {
      const { headers, samples } = await extractHeadersAndSamples(file);
      
      setCsvHeaders(headers);
      setSampleData(samples);
      setOriginalFile(file);
      
      const fileExtension = file.name.split('.').pop()?.toLowerCase();
      setOriginalFileFormat(fileExtension === 'csv' ? 'csv' : 'excel');
      
      setShowColumnMapping(true);
      
      try {
        if (fileInputRef.current) {
          fileInputRef.current.value = '';
        }
      } catch (error) {
        console.warn('Error clearing file input after upload:', error);
      }
    } catch (error: any) {
      notificationService.open({
        title: 'Error',
        text: error?.message || 'Failed to read file headers',
        type: 'danger'
      });
    }
  };

  const handleColumnMappingConfirm = async (mappings: Map<string, string>) => {
    setColumnMappings(mappings);
    setShowColumnMapping(false);
    
    if (!originalFile) return;
    
    try {
      const parsedData = await parseFile(originalFile, mappings);

      const dataWithStatus: VarBillingRow[] = parsedData.map(row => 
        ({ ...row, status: row.status || 'pending' as ProcessingStatus }));

      setTableData(dataWithStatus);
      
      notificationService.open({
        title: 'File Loaded',
        text: `Successfully loaded ${parsedData.length} rows`,
        type: 'success'
      });
    } catch (error: any) {
      notificationService.open({
        title: 'Error',
        text: error?.message || 'Failed to parse file',
        type: 'danger'
      });
    }
  };

  const handleColumnMappingCancel = () => {
    setShowColumnMapping(false);
    setOriginalFile(null);
    setOriginalFileFormat(null);
    setCsvHeaders([]);
    setSampleData([]);
  };

  const handleFileInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      handleFileUpload(file);
    }
  };

  const handleUploadButtonClick = () => {
    try {
      if (fileInputRef.current) {
        fileInputRef.current.click();
      }
    } catch (error) {
      console.warn('Error triggering file input click:', error);
    }
  };

  const handleClearAll = () => {
    setTableData([]);
    setOriginalFile(null);
    setOriginalFileFormat(null);
    setCsvHeaders([]);
    setSampleData([]);
    setColumnMappings(new Map());
    try {
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    } catch (error) {
      console.warn('Error clearing file input:', error);
    }
  };

  const handleSubmit = async () => {
    if (!originalFile || tableData.length === 0) return;

    const hasErrors = tableData.some(row => row.status === 'error');
    if (hasErrors) {
      notificationService.open({
        title: 'Validation Error',
        text: 'Please fix all errors before submitting',
        type: 'danger'
      });
      return;
    }

    setIsUploading(true);

    const successCallback = (data: { uploadId: string; fileName: string; numberOfRows: number }) => {
      onUploadSuccess(data);
      handleClearAll();
      onClose();
      notificationService.open({
        title: 'Upload Complete',
        text: 'File uploaded successfully',
        type: 'success'
      });
      setIsUploading(false);
    };

    const errorCallback = () => {
      notificationService.open({
        title: 'Upload Failed',
        text: 'Failed to upload file',
        type: 'danger'
      });
      setIsUploading(false);
    };

    onFileUpload({ 
      values: { 
        file: originalFile,
        columnMappings: columnMappings,
        numberOfRows: tableData.length
      }, 
      successCallback,
      errorCallback 
    });
  };

  const handleDownloadResults = () => {
    const timestamp = new Date().toISOString().replace(/[:.]/g, '-');

    if (originalFileFormat === 'excel') {
      const excelBuffer = generateExcel(tableData);
      const filename = `varbilling-upload-${timestamp}.xlsx`;
      downloadFile(excelBuffer, filename, 'excel');
    } else {
      const csv = generateCSV(tableData);
      const filename = `varbilling-upload-${timestamp}.csv`;
      downloadFile(csv, filename, 'csv');
    }
  };

  const handleDownloadTemplate = () => {
    const headers = ['External Contract ID', 'Customer Code', 'Product Code', 'Quantity', 'Price', 'Net', 'Comment'];
    const templateRow = ['CONTRACT001', 'CUST001', 'PROD001', '10', '100.00', '1000.00', 'Sample comment'];
    const csv = headers.join(',') + '\n' + templateRow.join(',');
    
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', 'VarBillingTemplate.csv');
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <AgModalWindow
      title="Variable Billing - Upload CSV/Excel"
      isOpen={isOpen}
      onClose={isUploading ? () => {} : onClose}
      contentStyle={{ 
        width: isMaximized ? '80vw' : '1000px', 
        maxHeight: isMaximized ? '1300px' : '1200px'
      }}
      maximizeState={{ isMaximized, setIsMaximized }}
    >
      <div className={styles['modal-content']}>
        
        <div className={styles['button-row']}>
          <input
            ref={fileInputRef}
            type="file"
            accept=".csv,.xlsx,.xls"
            onChange={handleFileInputChange}
            className={styles['hidden-file-input']}
          />
          <Button onClick={handleUploadButtonClick} disabled={isUploading}>
            Upload CSV/Excel
          </Button>
          <Button
            onClick={handleClearAll}
            disabled={tableData.length === 0 || isUploading}
          >
            Clear All
          </Button>
          <Button onClick={handleDownloadTemplate} disabled={isUploading}>
            Download Template
          </Button>
          {tableData.length > 0 && (
            <Button onClick={handleDownloadResults} disabled={isUploading}>
              Download Current Data
            </Button>
          )}
        </div>

        <div className={isMaximized ? styles['table-container-maximized'] : styles['table-container']}>
          <TableGrid
            ref={gridRef}
            columnDefs={columnDefs}
            rowData={tableData}
            pagination={false}
            paginationPageSize={undefined}
            suppressPaginationPanel={true}
            domLayout="normal"
            suppressHorizontalScroll={false}
            noRowsOverlayComponent={NoRowsOverlay}
            defaultColDef={{ resizable: true }}
            onGridReady={(params) => {
              gridRef.current = params.api;
              params.api.sizeColumnsToFit();
            }}
          />
        </div>

        {tableData.length > 0 && (
          <div className={styles['table-info']}>
            Showing {tableData.length} row{tableData.length !== 1 ? 's' : ''}
            {tableData.some(row => row.status === 'error') && (
              <span style={{ color: '#dc3545', marginLeft: '10px' }}>
                ({tableData.filter(row => row.status === 'error').length} with errors)
              </span>
            )}
          </div>
        )}

        <div className={styles['action-buttons']}>
          <Button 
            onClick={handleSubmit} 
            disabled={tableData.length === 0 || isUploading}
            buttonStyleType={ButtonStyleType.PRIMARY}
          >
            {isUploading ? 'Uploading...' : 'Submit'}
          </Button>
          <Button 
            onClick={onClose} 
            disabled={isUploading}
            buttonStyleType={ButtonStyleType.SECONDARY}
            className="ml-10"
          >
            Cancel
          </Button>
        </div>
      </div>
      
      <ColumnMappingModal
        isOpen={showColumnMapping}
        csvHeaders={csvHeaders}
        sampleData={sampleData}
        onConfirm={handleColumnMappingConfirm}
        onCancel={handleColumnMappingCancel}
      />
    </AgModalWindow>
  );
}
